import React, { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import apiMiddleware from "../../../api/apiMiddleware";
import Toster from "../../common/Toster";
import {
  AdminPageLayout,
  AdminPageHeader,
  AdminPageCard,
} from "../../common/AdminPageLayout";
import { ADMIN_PAGE_META } from "../../common/adminPageMeta";

const Settings = () => {
  const navigate = useNavigate();
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const [passwordData, setPasswordData] = useState({
    old_password: "",
    new_password: "",
    confirm_password: "",
  });

  const [showPasswords, setShowPasswords] = useState({
    old: false,
    new: false,
    confirm: false,
  });

  const [showPasswordSection, setShowPasswordSection] = useState(false);

  // Password change mutation
  const changePasswordMutation = useMutation({
    mutationFn: async (data) => {
      const response = await apiMiddleware.post("/auth/change-password", {
        old_password: data.old_password,
        new_password: data.new_password,
      });
      return response.data;
    },
    onSuccess: () => {
      setShowSuccess(true);
      setSuccessMessage("Password changed successfully");
      setPasswordData({
        old_password: "",
        new_password: "",
        confirm_password: "",
      });
      setShowPasswordSection(false);
    },
    onError: (error) => {
      setShowError(true);
      setErrorMessage(error.response?.data?.message || "Failed to change password");
    },
  });

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handlePasswordSubmit = (e) => {
    e.preventDefault();

    // Validate passwords match
    if (passwordData.new_password !== passwordData.confirm_password) {
      setShowError(true);
      setErrorMessage("New passwords do not match");
      return;
    }

    // Validate password length
    if (passwordData.new_password.length < 8) {
      setShowError(true);
      setErrorMessage("New password must be at least 8 characters long");
      return;
    }

    changePasswordMutation.mutate(passwordData);
  };

  const handleLogout = async () => {
    try {
      // Call logout API if available
      await apiMiddleware.post("/auth/logout");
    } catch (error) {
      // Even if API call fails, proceed with logout
      console.error("Logout API error:", error);
    } finally {
      // Clear local storage
      localStorage.removeItem("admin_token");
      localStorage.removeItem("admin_user");
      // Clear legacy keys too (avoid future clashes)
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      // Redirect to login
      window.location.href = "/login";
    }
  };

  return (
    <AdminPageLayout>
      {showSuccess && successMessage && (
        <Toster
          message={successMessage}
          type="success"
          duration={3000}
          onClose={() => {
            setShowSuccess(false);
            setSuccessMessage("");
          }}
        />
      )}

      {showError && errorMessage && (
        <Toster
          message={errorMessage}
          type="error"
          duration={3000}
          onClose={() => {
            setShowError(false);
            setErrorMessage("");
          }}
        />
      )}

      <AdminPageHeader
        title={ADMIN_PAGE_META.settings.title}
        subtitle={ADMIN_PAGE_META.settings.subtitle}
      />

      <AdminPageCard>
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-800">Change Password</h2>
              <p className="text-sm text-gray-600 mt-1">Update your account password</p>
            </div>
            <button
              onClick={() => {
                setShowPasswordSection(!showPasswordSection);
                if (showPasswordSection) {
                  setPasswordData({
                    old_password: "",
                    new_password: "",
                    confirm_password: "",
                  });
                  setShowError(false);
                  setErrorMessage("");
                }
              }}
              className="px-4 py-2 text-sm font-medium text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-md transition-colors"
            >
              {showPasswordSection ? "Cancel" : "Change Password"}
            </button>
          </div>

          {showPasswordSection && (
            <form onSubmit={handlePasswordSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Current Password <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type={showPasswords.old ? "text" : "password"}
                  name="old_password"
                  value={passwordData.old_password}
                  onChange={handlePasswordChange}
                  required
                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
                  placeholder="Enter current password"
                />
                <button
                  type="button"
                  onClick={() => setShowPasswords((prev) => ({ ...prev, old: !prev.old }))}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                >
                  <i className={`fi ${showPasswords.old ? "fi-rr-eye-crossed" : "fi-rr-eye"}`}></i>
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                New Password <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type={showPasswords.new ? "text" : "password"}
                  name="new_password"
                  value={passwordData.new_password}
                  onChange={handlePasswordChange}
                  required
                  minLength={8}
                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
                  placeholder="Enter new password (min 8 characters)"
                />
                <button
                  type="button"
                  onClick={() => setShowPasswords((prev) => ({ ...prev, new: !prev.new }))}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                >
                  <i className={`fi ${showPasswords.new ? "fi-rr-eye-crossed" : "fi-rr-eye"}`}></i>
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">Password must be at least 8 characters long</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Confirm New Password <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type={showPasswords.confirm ? "text" : "password"}
                  name="confirm_password"
                  value={passwordData.confirm_password}
                  onChange={handlePasswordChange}
                  required
                  minLength={8}
                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
                  placeholder="Confirm new password"
                />
                <button
                  type="button"
                  onClick={() => setShowPasswords((prev) => ({ ...prev, confirm: !prev.confirm }))}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                >
                  <i className={`fi ${showPasswords.confirm ? "fi-rr-eye-crossed" : "fi-rr-eye"}`}></i>
                </button>
              </div>
            </div>

            <div className="flex justify-end pt-2">
              <button
                type="submit"
                disabled={changePasswordMutation.isPending}
                className="px-6 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {changePasswordMutation.isPending ? "Changing..." : "Change Password"}
              </button>
            </div>
            </form>
          )}
        </div>
      </AdminPageCard>

      <AdminPageCard className="mt-6">
        <div className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-800">Logout</h2>
              <p className="text-sm text-gray-600 mt-1">Sign out from your account</p>
            </div>
            <button
              onClick={handleLogout}
              className="px-6 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors"
            >
              <i className="fi fi-rr-sign-out mr-2"></i>
              Logout
            </button>
          </div>
        </div>
      </AdminPageCard>
    </AdminPageLayout>
  );
};

export default Settings;

